import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles, X } from "lucide-react";

const DONE_KEY = "logitrak:demoTourDone";

const STEPS = [
  { sel: '[data-testid="action-queue"]', title: "Actions prioritaires", text: "Tout ce qui demande votre attention, trié par criticité : assurances échues, trackers hors ligne, batteries faibles… Cliquez sur une ligne pour voir les véhicules concernés, ou exportez le tout en PDF." },
  { sel: '[data-testid="kpi-active"]', title: "Santé de la flotte en un coup d'œil", text: "Véhicules actifs, hors ligne, sans activité, taux d'utilisation et distance totale sur la période. Chaque carte est cliquable pour le détail." },
  { sel: '[data-testid="period-selector"]', title: "Période d'analyse", text: "Aujourd'hui, 7 jours, 30 jours ou dates personnalisées : tous les chiffres du dashboard se recalculent instantanément." },
  { sel: '[data-testid="dashboard-tabs"]', title: "Les vues du dashboard", text: "Analyse flotte, Conducteurs & éco-conduite, Véhicules & documents : chaque onglet approfondit un domaine avec ses propres indicateurs." },
  { sel: '[data-testid="refresh-btn"]', title: "Des données toujours honnêtes", text: "L'âge des données est affiché en permanence. Un clic ici force une actualisation complète depuis la télématique." },
];

export const DemoTour = () => {
  const [step, setStep] = useState(null);
  const [rect, setRect] = useState(null);

  const measure = useCallback((idx) => {
    const el = document.querySelector(STEPS[idx].sel);
    if (!el) return null;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    return el;
  }, []);

  const goTo = useCallback((idx, dir = 1) => {
    let i = idx;
    while (i >= 0 && i < STEPS.length && !document.querySelector(STEPS[i].sel)) i += dir;
    if (i < 0 || i >= STEPS.length) { finish(); return; }
    setStep(i);
    measure(i);
    setTimeout(() => {
      const el = document.querySelector(STEPS[i].sel);
      if (el) setRect(el.getBoundingClientRect());
    }, 400);
  }, [measure]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = () => {
    localStorage.setItem(DONE_KEY, "1");
    setStep(null);
    setRect(null);
  };

  useEffect(() => {
    if (!localStorage.getItem(DONE_KEY)) {
      const t = setTimeout(() => goTo(0), 2500);
      return () => clearTimeout(t);
    }
  }, [goTo]);

  useEffect(() => {
    if (step === null) return;
    const onResize = () => {
      const el = document.querySelector(STEPS[step].sel);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [step]);

  if (step === null) {
    return (
      <button onClick={() => goTo(0)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-[#111] text-white text-xs font-medium px-4 py-2.5 rounded-full shadow-lg hover:bg-gray-800 transition-colors"
        data-testid="demo-tour-start">
        <Sparkles size={13} /> Visite guidée
      </button>
    );
  }

  const s = STEPS[step];
  const vw = window.innerWidth, vh = window.innerHeight;
  const popW = Math.min(360, vw - 32);
  const below = rect ? rect.bottom + 230 < vh : true;
  const popStyle = rect ? {
    width: popW,
    left: Math.min(Math.max(rect.left, 16), vw - popW - 16),
    top: below ? rect.bottom + 14 : undefined,
    bottom: below ? undefined : vh - rect.top + 14,
  } : { width: popW, left: vw / 2 - popW / 2, top: vh / 2 };

  return (
    <div className="fixed inset-0 z-[90]" data-testid="demo-tour-overlay">
      {rect && (
        <div className="absolute rounded-xl pointer-events-none transition-all duration-300"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
                   boxShadow: "0 0 0 9999px rgba(10,15,25,0.6)", border: "2px solid #fff" }} />
      )}
      <div className="absolute bg-white rounded-xl shadow-2xl p-4 transition-all duration-300" style={popStyle} data-testid="demo-tour-popover">
        <div className="flex items-start justify-between gap-3">
          <h4 className="text-sm font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{s.title}</h4>
          <button onClick={finish} className="p-1 -m-1 text-gray-400 hover:text-gray-600 rounded" data-testid="demo-tour-close" title="Fermer la visite">
            <X size={15} />
          </button>
        </div>
        <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{s.text}</p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-gray-900" : "bg-gray-300"}`} />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {step > 0 && (
              <button onClick={() => goTo(step - 1, -1)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" data-testid="demo-tour-prev">
                <ChevronLeft size={14} />
              </button>
            )}
            <button onClick={() => step + 1 >= STEPS.length ? finish() : goTo(step + 1)}
              className="flex items-center gap-1 bg-[#111] text-white text-xs font-medium px-3.5 py-1.5 rounded-lg hover:bg-gray-800"
              data-testid="demo-tour-next">
              {step + 1 >= STEPS.length ? "Terminer" : "Suivant"}
              {step + 1 < STEPS.length && <ChevronRight size={13} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
